import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Surf Packages', path: '/packages' },
  { label: 'Rooms', path: '/rooms' },
  { label: 'Activities', path: '/activities' },
  { label: 'Blog', path: '/blog' },
  { label: 'Store', path: '/store' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);



  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-white shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link
              to="/"
              className="flex shrink-0 items-center gap-2 group"
              id="nav-logo"
            >
              <img
                src="/images/logo_sable.webp" 
                alt="Sable Beach Logo" 
                className="h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              />
              <div className="hidden sm:flex items-center gap-1.5 ml-2">
                <span className="font-heading text-2xl tracking-tight text-[#0A193F] transition-colors duration-300">
                  SABLE
                </span>
                <span className="font-heading text-2xl tracking-tight text-[#1A4BE1] transition-colors duration-300">
                  BEACH
                </span>
              </div>
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-4 xl:gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.label}
                  to={link.path}
                  id={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="font-body text-sm font-medium tracking-wide uppercase transition-all duration-300 relative group text-dark-blue/70 hover:text-ocean"
                >
                  {link.label}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-ocean transition-all duration-300 ${
                    location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`} />
                </Link>
              ))}
              
              <Link
                to="/contact"
                className="font-body text-sm font-medium tracking-wide uppercase transition-all duration-300 relative group text-dark-blue/70 hover:text-ocean"
              >
                Contact
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-ocean transition-all duration-300 ${
                  location.pathname === '/contact' ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </Link>

              <Link
                to="/book-now"
                id="nav-book-now"
                className="btn-primary !py-3 !px-6 !text-sm"
              >
                Book Now
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden relative z-50 p-2"
              id="nav-mobile-toggle"
              aria-label="Toggle mobile menu"
            >
              {mobileOpen ? (
                <X className="w-6 h-6 text-[#0A193F]" />
              ) : (
                <Menu className="w-8 h-8 text-[#0A193F]" />
              )}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile menu overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-dark-blue/30 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />

            {/* Drawer */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="absolute right-0 top-0 bottom-0 w-80 max-w-[85vw] bg-sand shadow-2xl"
            >
              <div className="flex flex-col pt-28 px-8 gap-2">
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.label}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.05 }}
                  >
                    <Link
                      to={link.path}
                      className={`block font-body text-lg font-medium py-3 border-b border-dark-blue/10 transition-colors ${
                        location.pathname === link.path ? 'text-ocean' : 'text-dark-blue hover:text-ocean'
                      }`}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
                
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 + navLinks.length * 0.05 }}
                >
                  <Link
                    to="/contact"
                    className={`block font-body text-lg font-medium py-3 border-b border-dark-blue/10 transition-colors ${
                      location.pathname === '/contact' ? 'text-ocean' : 'text-dark-blue hover:text-ocean'
                    }`}
                  >
                    Contact
                  </Link>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <Link
                    to="/book-now"
                    className="btn-primary mt-6 text-center block w-full"
                  >
                    Book Now
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
